import { isEmpty } from "lodash-es";
import queryString from "query-string";

import { type IQueryParams } from "../types";

const buildImageUrl = (
  img?: {
    CFURL?: string;
    name?: string;
    signature?: string;
  },
  placeholder?: string
) =>
  img?.CFURL && img?.name && img?.signature
    ? `${img.CFURL}${img.name}${img.signature}`
    : placeholder;

const buildVideoUrl = (
  data?: {
    CFURL?: string;
    name?: string;
    signature?: string;
  },
  placeholder?: string
) => buildImageUrl(data, placeholder);

type VideoVariant = {
  src: string;
  type: string;
};

const VIDEO_VARIANTS = [
  { prefix: "2180", ext: "webm", mimeType: "video/webm" },
  { prefix: "1080", ext: "webm", mimeType: "video/webm" },
  { prefix: "720", ext: "mp4", mimeType: "video/mp4" },
] as const;

const buildVideoVariants = (data?: {
  CFURL?: string;
  name?: string;
  signature?: string;
}): VideoVariant[] => {
  if (!data?.CFURL || !data?.name || !data?.signature) return [];

  const nameWithoutExt = data.name.replace(/\.[^.]+$/, "");

  const variants: VideoVariant[] = VIDEO_VARIANTS.map(
    ({ prefix, ext, mimeType }) => ({
      src: `${data.CFURL}${prefix}_${nameWithoutExt}.${ext}${data.signature}`,
      type: mimeType,
    })
  );

  // Raw fallback
  variants.push({
    src: `${data.CFURL}${data.name}${data.signature}`,
    type: "video/mp4",
  });

  return variants;
};

const buildUrl = (path: string, params?: IQueryParams) => {
  if (!params || isEmpty(params)) {
    return path;
  }

  const apiParams: Record<string, string | number> = {
    limit: params.limit,
  };

  if (params.sortBy) {
    apiParams.sort = params.sortBy;
  }

  if (params.qty_bedrooms?.length) {
    apiParams.qty_bedrooms = params.qty_bedrooms.join(",");
  }

  if (params.highlights?.length) {
    apiParams.highlights = params.highlights.join(",");
  }

  if (params.nearBuilding?.length) {
    apiParams.nearBuilding = params.nearBuilding.join(",");
  }

  if (params.nearNeighbourhood?.length) {
    apiParams.nearNeighbourhood = params.nearNeighbourhood.join(",");
  }

  return `${path}?${queryString.stringify(apiParams, {
    arrayFormat: "comma",
  })}`;
};

export { buildImageUrl, buildVideoUrl, buildVideoVariants, buildUrl };
